import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import FadeIn from '../../components/FadeIn';

const Login = () => {
    const [loginIdentifier, setLoginIdentifier] = useState(''); // Email or Username
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { login, resetPassword } = useAuth();
    const navigate = useNavigate();

    // Forgot Password State
    const [showForgotModal, setShowForgotModal] = useState(false);
    const [resetEmail, setResetEmail] = useState('');
    const [resetError, setResetError] = useState('');
    const [resetMessage, setResetMessage] = useState('');
    const [resetLoading, setResetLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            setError('');
            setLoading(true);

            let emailToLogin = loginIdentifier.trim();

            // Check if input is NOT an email (simple check)
            if (!emailToLogin.includes('@')) {
                // Assume it's a username, fetch email from Firestore
                const q = query(collection(db, 'users'), where('username', '==', emailToLogin.toLowerCase()));
                const querySnapshot = await getDocs(q);

                if (querySnapshot.empty) {
                    throw new Error('Username not found');
                }

                emailToLogin = querySnapshot.docs[0].data().email;
            }

            const userCredential = await login(emailToLogin, password);
            const user = userCredential.user;

            // Update lastLogin in Firestore
            try {
                // We try to update, but if the user doc doesn't exist (e.g. old admin), it might fail or we should create it?
                // For now, let's attempt update.
                const userDocRef = doc(db, 'users', user.uid);
                // Check if doc exists first or just update? updateDoc fails if doesn't exist.
                // setDoc with merge is safer if we are unsure, but let's assume users exist or we handle legacy separately.
                // Given we just added users collection, the FIRST admin might not be in it?
                // Actually SetupAdmin didn't create a firestore doc, only Auth.
                // So we should use setDoc with merge: true to ensure the doc exists

                await import('firebase/firestore').then(({ setDoc }) =>
                    setDoc(userDocRef, {
                        lastLogin: serverTimestamp(),
                        email: emailToLogin, // Ensure email is there
                        // We might not have username for legacy admin, that's fine
                    }, { merge: true })
                );

            } catch (updateErr) {
                console.error("Failed to update last login", updateErr);
                // Don't block login on this
            }

            navigate('/admin/dashboard');
        } catch (err) {
            setError('Failed to log in: ' + err.message);
        }
        setLoading(false);
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();

        try {
            setResetError('');
            setResetMessage('');
            setResetLoading(true);
            await resetPassword(resetEmail);
            setResetMessage('Check your inbox for further instructions');
        } catch (err) {
            setResetError('Failed to reset password: ' + err.message);
        }
        setResetLoading(false);
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8">
                <FadeIn>
                    <div className="text-center">
                        <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
                            Admin Login
                        </h2>
                    </div>

                    {!showForgotModal ? (
                        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                            {error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">{error}</div>}
                            <div className="rounded-md shadow-sm -space-y-px">
                                <div>
                                    <label htmlFor="email-address" className="sr-only">Email address or Username</label>
                                    <input
                                        id="email-address"
                                        name="email"
                                        type="text"
                                        autoComplete="username"
                                        required
                                        className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-[#A2CB52] focus:border-[#A2CB52] focus:z-10 sm:text-sm"
                                        placeholder="Email address or Username"
                                        value={loginIdentifier}
                                        onChange={(e) => setLoginIdentifier(e.target.value)}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="password" className="sr-only">Password</label>
                                    <input
                                        id="password"
                                        name="password"
                                        type="password"
                                        autoComplete="current-password"
                                        required
                                        className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-[#A2CB52] focus:border-[#A2CB52] focus:z-10 sm:text-sm"
                                        placeholder="Password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="flex items-center justify-end">
                                <div className="text-sm">
                                    <button
                                        type="button"
                                        onClick={() => setShowForgotModal(true)}
                                        className="font-medium text-[#A2CB52] hover:text-[#8cb342]"
                                    >
                                        Forgot your password?
                                    </button>
                                </div>
                            </div>

                            <div>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-[#20222D] hover:bg-[#2a2d3d] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#A2CB52]"
                                >
                                    Sign in
                                </button>
                            </div>
                        </form>
                    ) : (
                        <div className="mt-8 space-y-6">
                            <div className="text-center mb-4">
                                <h3 className="text-lg font-medium text-gray-900">Reset Password</h3>
                                <p className="mt-1 text-sm text-gray-500">
                                    Enter your email address and we'll send you a link to reset your password.
                                </p>
                            </div>

                            <form className="space-y-6" onSubmit={handleResetPassword}>
                                {resetError && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">{resetError}</div>}
                                {resetMessage && <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative" role="alert">{resetMessage}</div>}

                                <div>
                                    <label htmlFor="reset-email" className="sr-only">Email address</label>
                                    <input
                                        id="reset-email"
                                        name="resetEmail"
                                        type="email"
                                        autoComplete="email"
                                        required
                                        className="appearance-none rounded relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-[#A2CB52] focus:border-[#A2CB52] focus:z-10 sm:text-sm"
                                        placeholder="Email address"
                                        value={resetEmail}
                                        onChange={(e) => setResetEmail(e.target.value)}
                                    />
                                </div>

                                <div>
                                    <button
                                        type="submit"
                                        disabled={resetLoading}
                                        className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-[#20222D] hover:bg-[#2a2d3d] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#A2CB52]"
                                    >
                                        Send Reset Link
                                    </button>
                                </div>

                                <div className="text-center">
                                    <button
                                        type="button"
                                        onClick={() => setShowForgotModal(false)}
                                        className="font-medium text-[#A2CB52] hover:text-[#8cb342] text-sm"
                                    >
                                        Back to Login
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}
                </FadeIn>
            </div>
        </div>
    );
};

export default Login;
